import React, { useState, useEffect } from 'react';
import '../../styles/modal.css';
import '../../styles/test-suites.css';

const AddAITestCasesModal = ({ isOpen, onClose, onAddTestCases, testSuiteId }) => {
  const [availableTestCases, setAvailableTestCases] = useState([]);
  const [selectedTestCases, setSelectedTestCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterPriority, setFilterPriority] = useState('all');

  // Fetch available AI-generated test cases
  useEffect(() => {
    if (isOpen) {
      // In a real application, this would be an API call
      // For this example, we'll use mock data
      setLoading(true);
      
      // Simulate API call delay
      setTimeout(() => {
        try {
          // Mock data for AI-generated test cases
          const mockTestCases = [
            {
              id: 'ai-tc-1',
              name: 'Verify login with valid credentials',
              description: 'Test that a user can log in with valid username and password',
              priority: 'high',
              steps: '1. Navigate to login page\n2. Enter valid username\n3. Enter valid password\n4. Click login button',
              expectedResult: 'User should be logged in and redirected to dashboard',
              generatedAt: '2025-06-25T14:30:00',
              aiConfidence: 95
            },
            {
              id: 'ai-tc-2',
              name: 'Verify login with invalid username',
              description: 'Test that appropriate error message is shown when logging in with invalid username',
              priority: 'medium',
              steps: '1. Navigate to login page\n2. Enter invalid username\n3. Enter valid password\n4. Click login button',
              expectedResult: 'Error message should be displayed indicating invalid credentials',
              generatedAt: '2025-06-25T14:31:00',
              aiConfidence: 92
            },
            {
              id: 'ai-tc-3',
              name: 'Verify login with invalid password',
              description: 'Test that appropriate error message is shown when logging in with invalid password',
              priority: 'medium',
              steps: '1. Navigate to login page\n2. Enter valid username\n3. Enter invalid password\n4. Click login button',
              expectedResult: 'Error message should be displayed indicating invalid credentials',
              generatedAt: '2025-06-25T14:32:00',
              aiConfidence: 93
            },
            {
              id: 'ai-tc-4',
              name: 'Verify password reset functionality',
              description: 'Test that a user can request a password reset',
              priority: 'high',
              steps: '1. Navigate to login page\n2. Click "Forgot Password" link\n3. Enter email address\n4. Click submit button',
              expectedResult: 'Confirmation message should be displayed indicating password reset email has been sent',
              generatedAt: '2025-06-25T14:33:00',
              aiConfidence: 88
            },
            {
              id: 'ai-tc-5',
              name: 'Verify remember me functionality',
              description: 'Test that the "Remember Me" checkbox keeps user logged in',
              priority: 'low',
              steps: '1. Navigate to login page\n2. Enter valid username\n3. Enter valid password\n4. Check "Remember Me" checkbox\n5. Click login button\n6. Close browser\n7. Reopen browser and navigate to application',
              expectedResult: 'User should still be logged in',
              generatedAt: '2025-06-25T14:34:00',
              aiConfidence: 85
            },
            {
              id: 'ai-tc-6',
              name: 'Verify logout functionality',
              description: 'Test that a user can log out',
              priority: 'medium',
              steps: '1. Log in with valid credentials\n2. Click logout button',
              expectedResult: 'User should be logged out and redirected to login page',
              generatedAt: '2025-06-25T14:35:00',
              aiConfidence: 97
            },
            {
              id: 'ai-tc-7',
              name: 'Verify session timeout',
              description: 'Test that a user is logged out after session timeout',
              priority: 'low',
              steps: '1. Log in with valid credentials\n2. Wait for session timeout period\n3. Attempt to navigate to a protected page',
              expectedResult: 'User should be redirected to login page',
              generatedAt: '2025-06-25T14:36:00',
              aiConfidence: 82
            }
          ];
          
          setAvailableTestCases(mockTestCases);
          setLoading(false);
        } catch (err) {
          setError('Failed to load AI-generated test cases. Please try again later.');
          setLoading(false);
        }
      }, 1000);
    }
  }, [isOpen]);

  // Handle search
  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  // Handle filter change
  const handleFilterChange = (priority) => {
    setFilterPriority(priority);
  };

  // Get filtered test cases
  const getFilteredTestCases = () => {
    return availableTestCases.filter(testCase => {
      // Filter by search query
      const matchesSearch = searchQuery === '' || 
        testCase.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        testCase.description.toLowerCase().includes(searchQuery.toLowerCase());
      
      // Filter by priority
      const matchesPriority = filterPriority === 'all' || testCase.priority === filterPriority;
      
      return matchesSearch && matchesPriority;
    });
  };

  // Handle selecting a test case
  const handleSelectTestCase = (testCase) => {
    if (selectedTestCases.some(tc => tc.id === testCase.id)) {
      // If already selected, remove it
      setSelectedTestCases(selectedTestCases.filter(tc => tc.id !== testCase.id));
    } else {
      // If not selected, add it
      setSelectedTestCases([...selectedTestCases, testCase]);
    }
  };

  // Handle adding selected test cases to the test suite
  const handleAddTestCases = () => {
    // Convert AI test cases to the format expected by the test suite
    const formattedTestCases = selectedTestCases.map(testCase => ({
      id: Date.now() + Math.random().toString(36).substr(2, 9), // Generate a unique ID
      name: testCase.name,
      description: testCase.description,
      priority: testCase.priority,
      status: 'pending',
      steps: testCase.steps,
      expectedResult: testCase.expectedResult,
      testSuiteId: testSuiteId,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      aiGenerated: true,
      aiConfidence: testCase.aiConfidence
    }));
    
    // Call the onAddTestCases callback with the formatted test cases
    onAddTestCases(formattedTestCases);
    
    // Reset selected test cases
    setSelectedTestCases([]);
    
    // Close modal
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      <div data-rt-modal-overlay className="modal-overlay" onClick={onClose}></div>
      <div className="modal add-test-cases-modal">
        <div className="modal-header">
          <h2>Add AI-Generated Test Cases</h2>
          <button className="modal-close" onClick={onClose}>
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
        <div className="modal-body">
          {loading ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>Loading AI-generated test cases...</p>
            </div>
          ) : error ? (
            <div className="error-container">
              <div className="error-icon">⚠️</div>
              <p>{error}</p>
            </div>
          ) : (
            <>
              <div className="test-cases-filter">
                <div className="search-container">
                  <input
                    type="text"
                    placeholder="Search test cases..."
                    value={searchQuery}
                    onChange={handleSearch}
                    className="search-input"
                  />
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="search-icon">
                    <circle cx="11" cy="11" r="8"></circle>
                    <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                  </svg>
                </div>
                <div className="filter-container">
                  <label>Filter by Priority:</label>
                  <div className="filter-buttons">
                    <button 
                      className={`filter-btn ${filterPriority === 'all' ? 'active' : ''}`}
                      onClick={() => handleFilterChange('all')}
                    >
                      All
                    </button>
                    <button 
                      className={`filter-btn ${filterPriority === 'high' ? 'active' : ''}`}
                      onClick={() => handleFilterChange('high')}
                    >
                      High
                    </button>
                    <button 
                      className={`filter-btn ${filterPriority === 'medium' ? 'active' : ''}`}
                      onClick={() => handleFilterChange('medium')}
                    >
                      Medium
                    </button>
                    <button 
                      className={`filter-btn ${filterPriority === 'low' ? 'active' : ''}`}
                      onClick={() => handleFilterChange('low')}
                    >
                      Low
                    </button>
                  </div>
                </div>
              </div>
              
              <div className="selection-summary">
                <span>{selectedTestCases.length} test cases selected</span>
              </div>
              
              <div className="ai-test-cases-list">
                {getFilteredTestCases().length === 0 ? (
                  <div className="empty-state">
                    <p>No test cases match your search criteria</p>
                  </div>
                ) : (
                  getFilteredTestCases().map(testCase => (
                    <div 
                      key={testCase.id} 
                      className={`ai-test-case-item ${selectedTestCases.some(tc => tc.id === testCase.id) ? 'selected' : ''}`}
                      onClick={() => handleSelectTestCase(testCase)}
                    >
                      <div className="test-case-header">
                        <div className="test-case-name-container">
                          <input 
                            type="checkbox" 
                            checked={selectedTestCases.some(tc => tc.id === testCase.id)}
                            onChange={() => {}} // Handled by the onClick of the parent div
                            onClick={(e) => e.stopPropagation()}
                          />
                          <h3 className="test-case-name">{testCase.name}</h3>
                        </div>
                        <div className="test-case-meta">
                          <span className={`priority-badge priority-${testCase.priority}`}>
                            {testCase.priority.charAt(0).toUpperCase() + testCase.priority.slice(1)}
                          </span>
                          <span className="ai-confidence">
                            AI Confidence: {testCase.aiConfidence}%
                          </span>
                        </div>
                      </div>
                      <p className="test-case-description">{testCase.description}</p>
                      <div className="test-case-details">
                        <div className="test-case-section">
                          <h4>Steps:</h4>
                          <pre>{testCase.steps}</pre>
                        </div>
                        <div className="test-case-section">
                          <h4>Expected Result:</h4>
                          <pre>{testCase.expectedResult}</pre>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </>
          )}
        </div>
        <div className="modal-footer">
          <div className="selection-count">
            {selectedTestCases.length} test cases selected
          </div>
          <div className="modal-actions">
            <button className="btn btn-outline" onClick={onClose}>Cancel</button>
            <button 
              className="btn btn-primary" 
              onClick={handleAddTestCases}
              disabled={selectedTestCases.length === 0}
            >
              Add Selected Test Cases
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddAITestCasesModal;